import Cookies from 'js-cookie';
import { STORAGE_KEYS } from './constants';

// Secure token manager using httpOnly cookies
export const SecureTokenManager = {
  // Get JWT token from sessionStorage (for WebSocket auth)
  getToken: (): string | null => {
    try {
      return sessionStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    } catch {
      return null;
    }
  },

  // Set JWT token in sessionStorage (for WebSocket auth)
  setToken: (token: string): void => {
    try {
      sessionStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
    } catch (error) {
      console.error("Failed to save token:", error);
    }
  },

  // For frontend-only storage (non-sensitive data)
  getNonSensitiveData: (): Record<string, unknown> | null => {
    try {
      const data = sessionStorage.getItem(STORAGE_KEYS.USER_DATA);
      return data ? JSON.parse(data) : null;
    } catch {
      return null;
    }
  },

  setNonSensitiveData: (data: Record<string, unknown>): void => {
    try {
      sessionStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(data));
    } catch (error) {
      console.error("Failed to save non-sensitive data:", error);
    }
  },

  removeNonSensitiveData: (): void => {
    try {
      sessionStorage.removeItem(STORAGE_KEYS.USER_DATA);
    } catch (error) {
      console.error("Failed to remove non-sensitive data:", error);
    }
  },

  // Check if user is authenticated by checking for stored user data
  // Note: httpOnly cookies are NOT accessible via JavaScript for security
  // We use sessionStorage user data as a proxy for authentication status
  isAuthenticated: (): boolean => {
    const userData = sessionStorage.getItem(STORAGE_KEYS.USER_DATA);
    return !!userData; // Returns true if user data exists
  },

  // Clear all authentication data
  clearAuth: (): void => {
    // Clear session storage (including JWT token for WebSocket)
    sessionStorage.removeItem(STORAGE_KEYS.USER_DATA);
    sessionStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);

    // Clear any remaining cookies
    Cookies.remove('auth_token', {
      path: '/',
      secure: true,
      sameSite: 'strict'
    });
    Cookies.remove('refresh_token', {
      path: '/',
      secure: true,
      sameSite: 'strict'
    });
  },

  // Store CSRF token for double submit cookie pattern
  getCSRFToken: (): string | null => {
    const metaTag = document.querySelector('meta[name="csrf-token"]');
    return metaTag?.getAttribute('content') || Cookies.get('csrf_token') || null;
  },

  setCSRFToken: (token: string): void => {
    // Store CSRF token in memory and cookie for double submit
    Cookies.set('csrf_token', token, {
      secure: true,
      sameSite: 'strict',
      httpOnly: false, // CSRF token needs to be accessible via JavaScript
      expires: 1 // 1 day
    });
  },
};

// Legacy JWT validation (remove once backend properly validates)
export const JWTUtils = {
  isExpired: (token: string): boolean => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const exp = payload.exp * 1000;
      return Date.now() >= exp;
    } catch {
      return true;
    }
  },

  shouldRefresh: (token: string): boolean => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const exp = payload.exp * 1000;
      const oneHour = 60 * 60 * 1000;
      return Date.now() >= exp - oneHour;
    } catch {
      return false;
    }
  },

  decodeToken: (token: string) => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload;
    } catch {
      return null;
    }
  },
};